import { useCallback, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { cn } from "@/lib/utils"
import { MetricLabel } from "../MetricLabel/MetricLabel"
import type { KpiTone } from "../types/backtest"

export interface PnlKpi {
  label: string
  value: string
  tone: KpiTone
  definition?: string
}

export interface PnlSeriesPoint {
  label: string
  profit: number
  loss: number
  cumulative: number
}

export interface PnlSeries {
  id: string
  label: string
  data: PnlSeriesPoint[]
  yFormat?: "money" | "number"
}

export interface DayPnlPoint {
  day: string
  profit: number
  loss: number
}

export interface PnlViewProps {
  kpis: PnlKpi[]
  seriesOptions: PnlSeries[]
  defaultSeriesId?: string
  dayOfWeek?: DayPnlPoint[]
  className?: string
}

const TOOLTIP_STYLE = {
  background: "var(--popover)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  fontSize: 11,
  color: "var(--foreground)",
  padding: "6px 8px",
} as const

const TONE_CLASS: Record<KpiTone, string> = {
  success: "text-success",
  danger: "text-danger",
  neutral: "text-muted-foreground",
  warning: "text-amber-500",
}

export function PnlView({
  kpis,
  seriesOptions,
  defaultSeriesId,
  dayOfWeek,
  className,
}: PnlViewProps) {
  const [seriesId, setSeriesId] = useState<string>(
    defaultSeriesId ?? seriesOptions[0]?.id ?? "",
  )
  const handleSelect = useCallback((id: string) => setSeriesId(id), [])
  const current = seriesOptions.find((s) => s.id === seriesId) ?? seriesOptions[0]

  const yFmt = (v: number) => {
    if (!current || current.yFormat === "number") return `${v}`
    return v < 0 ? `-$${Math.abs(v)}` : `$${v}`
  }

  return (
    <div className={className}>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 px-4 py-3 border-b border-border">
        {kpis.map((k) => (
          <div key={k.label} className="flex flex-col">
            <MetricLabel
              label={k.label}
              definition={k.definition}
              className="text-caption text-muted-foreground"
            />
            <span className={cn("text-body", TONE_CLASS[k.tone])}>{k.value}</span>
          </div>
        ))}
      </div>

      {current && (
        <div className="border-b border-border px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex flex-col gap-1">
              <div className="text-section text-foreground">Monthly P&amp;L</div>
              <div className="text-caption text-muted-foreground">
                Profit and loss with cumulative line
              </div>
            </div>
            <div className="flex items-center gap-1">
              {seriesOptions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleSelect(s.id)}
                  className={cn(
                    "text-caption px-2 py-0.5 rounded cursor-pointer transition-colors",
                    seriesId === s.id
                      ? "bg-muted text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart
              data={current.data}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                interval={2}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                width={60}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                tickFormatter={yFmt}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
                cursor={{ fill: "transparent" }}
              />
              <Bar dataKey="profit" fill="#26a69a" stackId="pnl" radius={[2, 2, 0, 0]} />
              <Bar dataKey="loss" fill="#f23645" stackId="pnl" radius={[0, 0, 2, 2]} />
              <Line
                type="monotone"
                dataKey="cumulative"
                stroke="var(--primary)"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {dayOfWeek && dayOfWeek.length > 0 && (
        <div className="border-b border-border px-4 py-4">
          <div className="flex flex-col gap-1 mb-2">
            <div className="text-section text-foreground">P&amp;L by Day</div>
            <div className="text-caption text-muted-foreground">
              Weekday breakdown
            </div>
          </div>

          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={dayOfWeek} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                width={60}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                tickFormatter={(v) => (v < 0 ? `-$${Math.abs(v)}` : `$${v}`)}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
                cursor={{ fill: "transparent" }}
              />
              <Bar dataKey="profit" fill="#26a69a" stackId="pnl" radius={[2, 2, 0, 0]} />
              <Bar dataKey="loss" fill="#f23645" stackId="pnl" radius={[0, 0, 2, 2]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
